<script setup lang="ts">
    import type { Column, Table } from '@tanstack/vue-table';
    import { computed, onBeforeUnmount, ref, watch } from 'vue';
    import { Button } from '@/components/ui/button';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuLabel,
        DropdownMenuSeparator,
        DropdownMenuTrigger,
    } from '@/components/ui/dropdown-menu';
    import { Eye, GripVertical } from 'lucide-vue-next';
    import draggable from 'vuedraggable';
    import AddButton from '@/components/AddButton.vue';
    import ViewOption from '@/components/ViewOption.vue';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';
    import { useViewOptionsStore } from '@/stores/view-options';
    import { Checkbox } from '@/components/ui/checkbox';
    import { Label } from '@/components/ui/label';

    interface DataTableViewOptionsProps {
        table: Table<any>;
    }

    const props = defineProps<DataTableViewOptionsProps>();

    const viewOptionsStore = useViewOptionsStore();

    const columns = computed(() =>
        props.table
            .getAllColumns()
            .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide()),
    );

    const unhideableColumns = computed(() =>
        props.table.getAllColumns().filter((column) => !column.getCanHide()),
    );

    // Helper function to get column header title
    const getColumnTitle = (column: any) => {
        const headerContext = { column };
        const headerContent = column.columnDef.header(headerContext);
        return headerContent?.props?.title || column.id;
    };

    const someColumnsVisible = computed(() => {
        const visibleCount =
            props.table.getVisibleFlatColumns().length - unhideableColumns.value.length;
        return visibleCount > 0 && visibleCount < columns.value.length;
    });

    const getColumn = (columnId: string): Column<any> | undefined =>
        props.table.getAllColumns().find((column) => column.id === columnId);

    // The built-in column showing, captured once. The (virtual) default view
    // restores this; it can't be edited by anyone.
    const builtInColumnVisibility = { ...props.table.getState().columnVisibility };

    // Only the owner of a stored view may change its columns/order. The default
    // view and views shared by other users are read-only.
    const canEdit = computed(
        () =>
            !!viewOptionsStore.activeViewOption?.is_owner &&
            !viewOptionsStore.activeViewOption?.is_default,
    );

    // Non-reorderable columns (e.g. select/id/name) must stay leading in the
    // column order so the reordered hideable columns line up after them.
    const fixedColumnIds = computed(() => unhideableColumns.value.map((column) => column.id));

    // Local order of the reorderable (hideable) columns, edited via drag and drop.
    const localColumnIds = ref<string[]>([]);

    const syncLocalOrder = () => {
        const hideableIds = columns.value.map((column) => column.id);
        const saved = viewOptionsStore.activeViewOption?.column_order ?? [];
        const ordered = saved.filter((id) => hideableIds.includes(id));
        const missing = hideableIds.filter((id) => !ordered.includes(id));
        localColumnIds.value = [...ordered, ...missing];
    };

    const fullColumnOrder = () => [...fixedColumnIds.value, ...localColumnIds.value];

    const applyColumnOrderToTable = () => {
        props.table.setColumnOrder(fullColumnOrder());
    };

    const persistColumnOrder = () => {
        if (!viewOptionsStore.activeViewOption) {
            return;
        }

        viewOptionsStore.updateColumnOrder(
            viewOptionsStore.activeViewOption.id,
            fullColumnOrder(),
        );
    };

    const handleDragEnd = () => {
        if (!canEdit.value) {
            return;
        }

        applyColumnOrderToTable();
        persistColumnOrder();
    };

    const timeout = ref<ReturnType<typeof setTimeout>>();

    const debouncedSaveColumnVisibility = () => {
        if (timeout.value) {
            clearTimeout(timeout.value);
        }

        timeout.value = setTimeout(() => {
            if (viewOptionsStore.activeViewOption) {
                const columnVisibility = props.table.getAllColumns().reduce(
                    (acc, column) => {
                        acc[column.id] = column.getIsVisible();
                        return acc;
                    },
                    {} as Record<string, boolean>,
                );

                viewOptionsStore.updateColumnVisibility(
                    viewOptionsStore.activeViewOption.id,
                    columnVisibility,
                );
            }
        }, 500);
    };

    const handleColumnVisibilityChange = (column: any, value: boolean) => {
        column.toggleVisibility(!!value);
        debouncedSaveColumnVisibility();
    };

    const handleColumnRowClick = (columnId: string) => {
        if (viewOptionsStore.editState || !canEdit.value) {
            return;
        }

        const column = getColumn(columnId);

        if (column) {
            handleColumnVisibilityChange(column, !column.getIsVisible());
        }
    };

    const handleToggleAllColumns = (value: boolean | 'indeterminate') => {
        if (!canEdit.value) {
            return;
        }

        const handler = props.table.getToggleAllColumnsVisibilityHandler();
        handler(value);
        debouncedSaveColumnVisibility();
    };

    const { isPending, isError, error, isSuccess, mutate } = useMutation({
        mutationFn: (newComment: {
            name: string;
            table_identifier: string;
            column_visibility: Record<string, boolean>;
            column_order: string[];
        }) => axios.post(route('v2.dashboard.view-options.store'), newComment),
        onSuccess: (data) => {
            viewOptionsStore.addViewOption(data.data);
            viewOptionsStore.setState('edit');
        },
    });

    function addViewOption() {
        mutate({
            name: '',
            table_identifier: 'employee-dashboard',
            column_visibility: props.table.getAllColumns().reduce(
                (acc, column) => {
                    acc[column.id] = column.getIsVisible();
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
            column_order: fullColumnOrder(),
        });
    }

    watch(
        () => viewOptionsStore.activeViewOption,
        (newOption) => {
            if (!newOption) {
                return;
            }

            const visibility = newOption.is_default
                ? { ...builtInColumnVisibility }
                : (newOption.column_visibility ?? {});

            props.table.setColumnVisibility(visibility);
        },
        { immediate: true },
    );

    watch(
        () => viewOptionsStore.activeViewOption?.id,
        () => {
            syncLocalOrder();
            applyColumnOrderToTable();
        },
        { immediate: true },
    );

    onBeforeUnmount(() => clearTimeout(timeout.value));
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <Button
                variant="outline"
                size="xs"
                class="normal-case"
            >
                <Eye />
                <template v-if="!viewOptionsStore.activeViewOption?.is_default">
                    {{ viewOptionsStore.activeViewOption?.name }}
                </template>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
            align="end"
            class="min-w-xs"
        >
            <DropdownMenuLabel>
                {{ $t('datatables.view_options.global_views') }}
            </DropdownMenuLabel>
            <ViewOption
                v-for="viewOption in viewOptionsStore.globalViewOptions"
                :key="viewOption.id"
                :view-option="viewOption"
                @keydown.stop
            />
            <template v-if="viewOptionsStore.personalViewOptions.length">
                <DropdownMenuSeparator class="mx-2" />
                <DropdownMenuLabel>
                    {{ $t('datatables.view_options.personal_views') }}
                </DropdownMenuLabel>
                <ViewOption
                    v-for="viewOption in viewOptionsStore.personalViewOptions"
                    :key="viewOption.id"
                    :view-option="viewOption"
                    @keydown.stop
                />
            </template>
            <AddButton
                @click="addViewOption"
                class="w-full"
            >
                {{ $t('datatables.view_options.add_option') }}
            </AddButton>
            <div v-if="viewOptionsStore.activeViewOption">
                <DropdownMenuLabel class="text-muted-foreground mt-2 font-normal">
                    {{ $t('datatables.view_options.choose_columns') }}
                </DropdownMenuLabel>
                <DropdownMenuSeparator class="mx-2" />
                <Label
                    v-if="canEdit"
                    class="flex flex-row items-center gap-2 px-2 py-1.5 font-normal"
                >
                    <Checkbox
                        :model-value="
                            table.getIsAllColumnsVisible()
                                ? true
                                : someColumnsVisible
                                  ? 'indeterminate'
                                  : false
                        "
                        @update:model-value="handleToggleAllColumns"
                        aria-label="Toggle all columns"
                    />
                    <span class="text-muted-foreground hover:text-primary cursor-pointer">
                        {{
                            table.getIsAllColumnsVisible()
                                ? $t('forms.unselect_all')
                                : $t('forms.select_all')
                        }}
                    </span>
                </Label>

                <draggable
                    v-model="localColumnIds"
                    item-key="columnId"
                    handle=".column-drag-handle"
                    :disabled="!canEdit"
                    :animation="150"
                    ghost-class="opacity-50"
                    tag="div"
                    @end="handleDragEnd"
                >
                    <template #item="{ element: columnId }">
                        <div
                            v-if="getColumn(columnId)"
                            class="group flex flex-row items-stretch text-xs select-none"
                            :class="{
                                'pointer-events-none opacity-50': viewOptionsStore.editState,
                            }"
                        >
                            <div
                                class="flex min-h-8 flex-1 items-center gap-2 py-1.5 pl-2"
                                :class="canEdit ? 'cursor-pointer' : 'cursor-default'"
                                @click="handleColumnRowClick(columnId)"
                            >
                                <Checkbox
                                    :model-value="getColumn(columnId)?.getIsVisible()"
                                    :disabled="!canEdit"
                                    class="pointer-events-none"
                                    :class="{ 'grayscale opacity-60': !canEdit }"
                                    aria-hidden="true"
                                    tabindex="-1"
                                />
                                <span
                                    class="text-muted-foreground"
                                    :class="{ 'group-hover:text-primary': canEdit }"
                                >
                                    {{ $t(getColumnTitle(getColumn(columnId))) }}
                                </span>
                            </div>
                            <div
                                v-if="canEdit"
                                class="column-drag-handle text-muted-foreground group-hover:text-primary flex shrink-0 cursor-grab items-center self-stretch px-2 active:cursor-grabbing"
                                @click.stop
                            >
                                <GripVertical class="size-3.5" />
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
